/**
 * Centralized US Market Hours, Holidays & ET Time Helpers
 *
 * Single source of truth for:
 * - Eastern Time conversion (DST-aware via Intl API)
 * - US stock market holidays (fully algorithmic, no yearly updates)
 * - Market session detection (regular, pre-market, after-hours, closed)
 * - Crypto symbol detection
 */

// ============================================================================
// Types
// ============================================================================

/** Eastern Time components, DST-aware */
export interface ETTime {
  hour: number;       // 0-23
  minute: number;     // 0-59
  dayOfWeek: number;  // 0=Sunday, 6=Saturday
  month: number;      // 1-12
  day: number;        // 1-31
  year: number;
}

/** Stock market session status */
export type MarketSession = "closed" | "pre" | "open" | "ah";

/** Holiday info */
export interface MarketHoliday {
  name: string;
  earlyClose?: boolean;  // true for half-days (1 PM close)
}

// ============================================================================
// ET Time Helpers
// ============================================================================

/** Get current Eastern Time components (DST-aware via Intl API) */
export function getETTime(date?: Date): ETTime {
  const now = date ?? new Date();
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    hour: "numeric",
    minute: "numeric",
    hour12: false,
    weekday: "short",
    month: "numeric",
    day: "numeric",
    year: "numeric",
  }).formatToParts(now);

  let hour = 0, minute = 0, dayOfWeek = 0, month = 1, day = 1, year = 2026;
  const dayMap: Record<string, number> = {
    Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6,
  };

  for (const part of parts) {
    switch (part.type) {
      case "hour": hour = parseInt(part.value, 10); break;
      case "minute": minute = parseInt(part.value, 10); break;
      case "weekday": dayOfWeek = dayMap[part.value] ?? 0; break;
      case "month": month = parseInt(part.value, 10); break;
      case "day": day = parseInt(part.value, 10); break;
      case "year": year = parseInt(part.value, 10); break;
    }
  }

  // Intl API can return hour=24 for midnight in some environments
  if (hour === 24) hour = 0;

  return { hour, minute, dayOfWeek, month, day, year };
}

/** Get current ET hour (convenience) */
export function getETHour(date?: Date): number {
  return getETTime(date).hour;
}

/** Get current ET minute (convenience) */
export function getETMinute(date?: Date): number {
  return getETTime(date).minute;
}

/** Get current ET day of week (convenience, 0=Sun 6=Sat) */
export function getETDayOfWeek(date?: Date): number {
  return getETTime(date).dayOfWeek;
}

// ============================================================================
// Forex Daily Boundary (DST-aware)
// ============================================================================

/**
 * Determine if a UTC date falls within US Eastern Daylight Time (EDT).
 * Pure algorithmic — no Intl API needed, safe for Cloudflare Workers.
 *
 * US DST rules (since 2007):
 *   Starts: 2nd Sunday of March at 2:00 AM ET (= 07:00 UTC)
 *   Ends:   1st Sunday of November at 2:00 AM ET (= 06:00 UTC, since during EDT UTC offset is -4)
 */
export function isUSEDT(date: Date): boolean {
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth() + 1; // 1-12

  if (month < 3 || month > 11) return false; // Jan, Feb, Dec → EST
  if (month > 3 && month < 11) return true;  // Apr–Oct → EDT

  if (month === 3) {
    // 2nd Sunday of March
    const mar1dow = new Date(Date.UTC(year, 2, 1)).getUTCDay();
    const secondSunday = 1 + ((7 - mar1dow) % 7) + 7;
    const dstStart = Date.UTC(year, 2, secondSunday, 7, 0, 0); // 2 AM EST = 07:00 UTC
    return date.getTime() >= dstStart;
  }

  // month === 11
  const nov1dow = new Date(Date.UTC(year, 10, 1)).getUTCDay();
  const firstSunday = 1 + ((7 - nov1dow) % 7);
  const dstEnd = Date.UTC(year, 10, firstSunday, 6, 0, 0); // 2 AM EDT = 06:00 UTC
  return date.getTime() < dstEnd;
}

/**
 * Get the forex daily candle boundary hour in UTC for a given date.
 * 5 PM ET = 22:00 UTC (EST, Nov–Mar) or 21:00 UTC (EDT, Mar–Nov).
 */
export function getForexDailyBoundaryHour(date?: Date): number {
  return isUSEDT(date ?? new Date()) ? 21 : 22;
}

// ============================================================================
// Holiday Algorithm (fully algorithmic — no yearly updates needed)
// ============================================================================

/**
 * Compute Easter Sunday using the Anonymous Gregorian algorithm.
 * Valid for all Gregorian calendar years.
 */
function getEasterSunday(year: number): { month: number; day: number } {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31); // 3=March, 4=April
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return { month, day };
}

/**
 * Get the Nth occurrence of a weekday in a month.
 * weekday: 0=Sunday, 1=Monday, ..., 6=Saturday
 * n: 1=first, 2=second, 3=third, 4=fourth
 */
function getNthWeekday(year: number, month: number, weekday: number, n: number): number {
  // First day of the month
  const first = new Date(year, month - 1, 1);
  const firstDow = first.getDay();
  // Days until first occurrence of the target weekday
  let dayOfMonth = 1 + ((weekday - firstDow + 7) % 7);
  // Advance to the nth occurrence
  dayOfMonth += (n - 1) * 7;
  return dayOfMonth;
}

/**
 * Get the last occurrence of a weekday in a month.
 */
function getLastWeekday(year: number, month: number, weekday: number): number {
  // Last day of the month
  const lastDay = new Date(year, month, 0).getDate();
  const last = new Date(year, month - 1, lastDay);
  const lastDow = last.getDay();
  const diff = (lastDow - weekday + 7) % 7;
  return lastDay - diff;
}

/**
 * Apply the "observed" rule for fixed-date holidays:
 * - Falls on Saturday → observed Friday
 * - Falls on Sunday → observed Monday
 */
function getObservedDate(year: number, month: number, day: number): { month: number; day: number } {
  const date = new Date(year, month - 1, day);
  const dow = date.getDay();
  if (dow === 6) {
    // Saturday → Friday
    const fri = new Date(date);
    fri.setDate(fri.getDate() - 1);
    return { month: fri.getMonth() + 1, day: fri.getDate() };
  }
  if (dow === 0) {
    // Sunday → Monday
    const mon = new Date(date);
    mon.setDate(mon.getDate() + 1);
    return { month: mon.getMonth() + 1, day: mon.getDate() };
  }
  return { month, day };
}

/** Format as "YYYY-MM-DD" for cache key */
function dateKey(year: number, month: number, day: number): string {
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/** Check if a date is a weekday (Mon-Fri) */
function isWeekdayDate(year: number, month: number, day: number): boolean {
  const dow = new Date(year, month - 1, day).getDay();
  return dow >= 1 && dow <= 5;
}

/**
 * Build the holiday map for a given year.
 * All holidays are computed algorithmically — no static data needed.
 */
function buildHolidayMap(year: number): Map<string, MarketHoliday> {
  const map = new Map<string, MarketHoliday>();

  function add(m: number, d: number, name: string, earlyClose?: boolean) {
    map.set(dateKey(year, m, d), { name, earlyClose });
  }

  // --- Full-day closures ---

  // New Year's Day (Jan 1, observed)
  const ny = getObservedDate(year, 1, 1);
  add(ny.month, ny.day, "New Year's Day");

  // MLK Day (3rd Monday of January)
  add(1, getNthWeekday(year, 1, 1, 3), "MLK Day");

  // Presidents' Day (3rd Monday of February)
  add(2, getNthWeekday(year, 2, 1, 3), "Presidents' Day");

  // Good Friday (Friday before Easter)
  const easter = getEasterSunday(year);
  const easterDate = new Date(year, easter.month - 1, easter.day);
  const goodFriday = new Date(easterDate);
  goodFriday.setDate(goodFriday.getDate() - 2);
  add(goodFriday.getMonth() + 1, goodFriday.getDate(), "Good Friday");

  // Memorial Day (last Monday of May)
  add(5, getLastWeekday(year, 5, 1), "Memorial Day");

  // Juneteenth (Jun 19, observed)
  const jt = getObservedDate(year, 6, 19);
  add(jt.month, jt.day, "Juneteenth");

  // Independence Day (Jul 4, observed)
  const july4 = getObservedDate(year, 7, 4);
  add(july4.month, july4.day, "Independence Day");

  // Labor Day (1st Monday of September)
  add(9, getNthWeekday(year, 9, 1, 1), "Labor Day");

  // Thanksgiving (4th Thursday of November)
  const thanksgivingDay = getNthWeekday(year, 11, 4, 4);
  add(11, thanksgivingDay, "Thanksgiving");

  // Christmas (Dec 25, observed)
  const xmas = getObservedDate(year, 12, 25);
  add(xmas.month, xmas.day, "Christmas");

  // --- Early close days (1 PM ET) ---

  // Day before Independence Day (Jul 3 if weekday and not already a full holiday)
  if (isWeekdayDate(year, 7, 3) && !map.has(dateKey(year, 7, 3))) {
    add(7, 3, "Independence Day Eve", true);
  }

  // Black Friday (day after Thanksgiving)
  const blackFriday = thanksgivingDay + 1;
  add(11, blackFriday, "Black Friday", true);

  // Christmas Eve (Dec 24 if weekday and not already a full holiday)
  if (isWeekdayDate(year, 12, 24) && !map.has(dateKey(year, 12, 24))) {
    add(12, 24, "Christmas Eve", true);
  }

  return map;
}

// Module-level cache — holidays computed once per year
let holidayCache: Map<string, MarketHoliday> | null = null;
let holidayCacheYear = 0;

function getHolidayMap(year: number): Map<string, MarketHoliday> {
  if (holidayCache && holidayCacheYear === year) return holidayCache;
  holidayCache = buildHolidayMap(year);
  holidayCacheYear = year;
  return holidayCache;
}

// ============================================================================
// Holiday API
// ============================================================================

/** Check if a date is a US stock market holiday. Returns holiday info or null. */
export function getMarketHoliday(date?: Date): MarketHoliday | null {
  const et = getETTime(date);
  const map = getHolidayMap(et.year);
  return map.get(dateKey(et.year, et.month, et.day)) ?? null;
}

/** Check if today is a market holiday (full closure, not early close) */
export function isMarketHoliday(date?: Date): boolean {
  const h = getMarketHoliday(date);
  return h !== null && !h.earlyClose;
}

/** Check if today is an early close day (1 PM ET) */
export function isEarlyCloseDay(date?: Date): boolean {
  const h = getMarketHoliday(date);
  return h?.earlyClose === true;
}

/** Get market close time in minutes since midnight ET (960 = 4 PM, 780 = 1 PM on early close) */
export function getMarketCloseMinutes(date?: Date): number {
  return isEarlyCloseDay(date) ? 780 : 960;
}

// ============================================================================
// Market Hours
// ============================================================================

/** Check if weekend (Sat or Sun in ET) */
export function isWeekend(date?: Date): boolean {
  const dow = getETDayOfWeek(date);
  return dow === 0 || dow === 6;
}

/**
 * Check if US stock market is in regular trading hours.
 * Mon-Fri 9:30 AM - 4:00 PM ET (or 1:00 PM on early close days).
 * Returns false on weekends and holidays.
 */
export function isUSMarketOpen(date?: Date): boolean {
  const et = getETTime(date);
  if (et.dayOfWeek === 0 || et.dayOfWeek === 6) return false;
  if (isMarketHoliday(date)) return false;

  const minutes = et.hour * 60 + et.minute;
  const closeMinutes = getMarketCloseMinutes(date);
  return minutes >= 570 && minutes < closeMinutes; // 570 = 9:30 AM
}

/**
 * Get stock market session status (holiday-aware, early-close-aware).
 * Returns: "closed" | "pre" | "open" | "ah"
 */
export function getStockMarketSession(date?: Date): MarketSession {
  const et = getETTime(date);

  // Weekends
  if (et.dayOfWeek === 0 || et.dayOfWeek === 6) return "closed";

  // Full-day holidays
  if (isMarketHoliday(date)) return "closed";

  const minutes = et.hour * 60 + et.minute;
  const closeMinutes = getMarketCloseMinutes(date);

  // Pre-market: 4:00 AM - 9:30 AM ET
  if (minutes >= 240 && minutes < 570) return "pre";

  // Regular hours: 9:30 AM - close (4 PM or 1 PM on early close)
  if (minutes >= 570 && minutes < closeMinutes) return "open";

  // After hours: close - 8:00 PM ET
  if (minutes >= closeMinutes && minutes < 1200) return "ah";

  return "closed";
}

/**
 * Check if stock price data could be flowing (extended hours for polling).
 * Mon-Fri 8:00 AM - 5:00 PM ET, excluding full holidays.
 * Used by RealtimePriceProvider to gate stock price polling.
 */
export function isExtendedHoursActive(date?: Date): boolean {
  const et = getETTime(date);
  if (et.dayOfWeek === 0 || et.dayOfWeek === 6) return false;
  if (isMarketHoliday(date)) return false;
  return et.hour >= 8 && et.hour < 17;
}

/**
 * Check if stock data could be stale and needs refresh (wide window).
 * Mon-Fri 4:00 AM - 8:00 PM ET, excluding full holidays.
 * Used by needsFreshBars() in R2 client for write-through cache.
 */
export function isInTradingWindow(date?: Date): boolean {
  const et = getETTime(date);
  if (et.dayOfWeek === 0 || et.dayOfWeek === 6) return false;
  if (isMarketHoliday(date)) return false;
  return et.hour >= 4 && et.hour <= 20;
}

// ============================================================================
// Symbol Classification
// ============================================================================

/** Known crypto tickers for plain-symbol detection (without /USD suffix) */
export const CRYPTO_TICKERS: ReadonlySet<string> = new Set([
  "BTC", "ETH", "XRP", "BNB", "SOL", "DOGE", "ADA", "TRX", "AVAX", "LINK",
  "TON", "SHIB", "DOT", "BCH", "LTC", "UNI", "NEAR", "ICP", "APT", "ETC",
  "RENDER", "FET", "XMR", "STX", "CRO", "HBAR", "FIL", "ARB", "ATOM", "IMX",
  "VET", "INJ", "OP", "GRT", "THETA", "RUNE", "AAVE", "LDO", "ALGO", "FLOW",
  "SUI", "SEI", "XLM", "QNT", "AXS", "SAND", "MANA", "SNX", "CRV", "EGLD",
  "XTZ", "CHZ", "GALA", "APE", "NEO", "MINA", "ZEC", "CAKE", "IOTA", "ENJ",
  "COMP", "GMX", "DYDX", "TIA", "PEPE", "WLD", "BLUR", "PYTH", "STRK", "ORDI",
  "CFX", "FXS", "HNT", "1INCH", "ANKR", "BAT", "ENS", "LPT", "STORJ", "SUSHI",
  "YFI", "ZRX", "AR", "PENDLE", "ONDO", "ENA", "TAO", "MATIC",
]);

/** Forex pairs — use TwelveData, NOT Kraken. Trade 24/5. */
export const FOREX_SYMBOLS: ReadonlySet<string> = new Set([
  "EUR/USD", "GBP/USD", "USD/JPY", "USD/CAD", "AUD/USD",
  "USD/CHF", "NZD/USD", "EUR/GBP", "EUR/JPY", "GBP/JPY",
]);

/** Commodity spot prices — use TwelveData, NOT Kraken. */
export const COMMODITY_SYMBOLS: ReadonlySet<string> = new Set([
  "XAU/USD", "XAG/USD", "WTI/USD", "XBR/USD", "XPT/USD", "XPD/USD",
]);

/**
 * Check if a symbol is a crypto asset.
 * Only matches unambiguous formats: "BTC/USD" slash format.
 * Plain tickers like "BTC" are NOT matched — they're ambiguous (BTC is also
 * a stock ticker). Use CRYPTO_TICKERS directly for user-input normalization.
 * Excludes forex and commodity pairs that also use "/" format.
 */
export function isCryptoSymbol(symbol: string): boolean {
  const upper = symbol.toUpperCase();
  if (FOREX_SYMBOLS.has(upper) || COMMODITY_SYMBOLS.has(upper)) return false;
  if (upper.includes("/")) return true;
  return false;
}

/** Check if a symbol is a forex pair */
export function isForexSymbol(symbol: string): boolean {
  return FOREX_SYMBOLS.has(symbol.toUpperCase());
}

/** Check if a symbol is a commodity spot price */
export function isCommoditySymbol(symbol: string): boolean {
  return COMMODITY_SYMBOLS.has(symbol.toUpperCase());
}

/** Get the data source for a symbol. Crypto → Kraken, everything else → TwelveData. */
export function getDataSource(symbol: string): "kraken" | "twelvedata" {
  return isCryptoSymbol(symbol) ? "kraken" : "twelvedata";
}

/** Returns true if the symbol trades around the clock on weekdays (crypto 24/7, forex/commodities 24/5). */
export function is24HourMarket(symbol: string): boolean {
  return isCryptoSymbol(symbol) || isForexSymbol(symbol) || isCommoditySymbol(symbol);
}

/** Returns true if the symbol uses Monday-based weekly candles (crypto, forex, commodities). Stocks use Sunday. */
export function useMondayWeekStart(symbol: string): boolean {
  return isCryptoSymbol(symbol) || isForexSymbol(symbol) || isCommoditySymbol(symbol);
}

/**
 * Check if a bar timestamp falls on a weekend (market closed).
 * Forex/commodities open Sunday at 5 PM ET — DST-aware (21:00 UTC in EDT, 22:00 UTC in EST).
 * Saturday: always closed. Sunday before boundary: closed. Sunday at/after boundary: market open.
 */
export function isWeekendBar(timestamp: string): boolean {
  const normalized = timestamp.replace("Z", "").replace("T", " ");
  const d = new Date(normalized + "Z");
  const dow = d.getUTCDay();
  if (dow === 6) return true; // Saturday
  if (dow === 0) {
    const boundary = getForexDailyBoundaryHour(d);
    return d.getUTCHours() < boundary; // Sunday before market open
  }
  return false;
}
